import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'

import { type DevelopmentTask, visibleTaskElapsedMs } from '../../../app/development-tasks'
import { duration } from '../../../app/format'
import { eventsForProject, reduceLiveEvents } from '../../../app/live-activity'
import { useLiveActivity } from '../../../app/live-events'
import { useProject, useProjectDevelopmentTasks } from '../../../app/queries'
import { t } from '../../../copy'
import { T } from '../../../copy/text'
import { pj, type PjshellId } from '../../../copy/pjshell'
import { PJ } from '../../../copy/pjshell-text'
import { DevelopmentTaskCreate } from '../../development-task'
import { useProjectId } from './params'
import {
  TASK_USER_STATES,
  inboxListQuery,
  taskNextActionOf,
  taskUserStateOf,
  userStateCopy,
  type InboxUpdatedPreset,
  type TaskUserState,
} from './task-inbox'
import {
  DataCell,
  DataTable,
  EmptyState,
  FilterBar,
  ModuleError,
  ModuleHeader,
  ModulePage,
  ModuleSkeleton,
  StatusPill,
  Unverified,
  dataRowClass,
  moduleTitle,
} from './ui'

const TASK_GRID = 'minmax(0,1.5fr) 92px 74px 132px 84px minmax(0,1fr)'

type InboxFilter = 'all' | TaskUserState

export function ProjectTasksPage() {
  const projectId = useProjectId()
  if (!projectId) {
    return (
      <ModulePage testid="project-module-tasks">
        <ModuleSkeleton />
      </ModulePage>
    )
  }
  return <Loaded projectId={projectId} />
}

export { ProjectTasksPage as ProjectTasks }

function Loaded({ projectId }: { projectId: string }) {
  const project = useProject(projectId)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<InboxFilter>('all')
  const [assignee, setAssignee] = useState('')
  const [updatedPreset, setUpdatedPreset] = useState<InboxUpdatedPreset>('any')
  const listQuery = useMemo(
    () => inboxListQuery({ userState: filter, assignee, updatedPreset }),
    [filter, assignee, updatedPreset],
  )
  const tasks = useProjectDevelopmentTasks(projectId, listQuery)
  const live = useLiveActivity()
  const latestByTask = useMemo(
    () => reduceLiveEvents(eventsForProject(live.events, projectId)).latestByTask,
    [live.events, projectId],
  )

  if (project.isPending || tasks.isPending) {
    return (
      <ModulePage testid="project-module-tasks">
        <ModuleSkeleton />
      </ModulePage>
    )
  }
  if (project.error) {
    return (
      <ModulePage testid="project-module-tasks">
        <ModuleError message={project.error.message} />
      </ModulePage>
    )
  }
  if (tasks.error) {
    return (
      <ModulePage testid="project-module-tasks">
        <ModuleError message={tasks.error.message} />
      </ModulePage>
    )
  }
  if (!project.data) {
    return (
      <ModulePage testid="project-module-tasks">
        <ModuleSkeleton />
      </ModulePage>
    )
  }

  const items = tasks.data ?? []
  const needle = query.trim().toLowerCase()
  const visible = items.filter((task) => {
    if (needle === '') return true
    const files = task.context_items.map((item) => item.title).join(' ')
    return `${task.title} ${files}`.toLowerCase().includes(needle)
  })

  return (
    <ModulePage testid="project-module-tasks">
      <ModuleHeader
        title={moduleTitle(project.data.name, t('nav.ptab.tasks'))}
        subtitle={<PJ id="pjshell.task.subtitle" />}
        actions={<DevelopmentTaskCreate project={project.data} surface="lulu" />}
      />

      <div data-testid="project-task-inbox" className="min-w-0 max-w-[390px] sm:max-w-none">
        <FilterBar
          search={{ value: query, onChange: setQuery }}
          searchPlaceholder={pj('pjshell.task.search.placeholder')}
          searchLabel={pj('pjshell.task.search.placeholder')}
          filters={(['all', ...TASK_USER_STATES] as const).map((key) => ({
            id: key,
            label: filterLabel(key),
            selected: filter === key,
            onSelect: () => setFilter(key),
          }))}
        />
        <div className="mb-npc-grid flex flex-wrap items-center gap-npc-gap-sm">
          <label className="flex h-[30px] min-w-0 items-center gap-npc-gap-xs rounded-full border border-npc-lulu-line-2 bg-npc-lulu-bg-1 px-3">
            <span className="shrink-0 text-[12.5px] font-medium text-npc-lulu-t3">
              <PJ id="pjshell.task.filter.assignee" />
            </span>
            <input
              data-testid="project-task-filter-assignee"
              value={assignee}
              onChange={(event) => setAssignee(event.target.value)}
              placeholder={pj('pjshell.task.filter.assignee.placeholder')}
              className="h-full min-w-[140px] border-0 bg-transparent text-[12.5px] font-medium text-npc-lulu-t1 outline-none placeholder:text-npc-lulu-t4"
            />
          </label>
          <label className="flex h-[30px] items-center gap-npc-gap-xs rounded-full border border-npc-lulu-line-2 bg-npc-lulu-bg-1 px-3">
            <span className="shrink-0 text-[12.5px] font-medium text-npc-lulu-t3">
              <PJ id="pjshell.task.filter.updated" />
            </span>
            <select
              data-testid="project-task-filter-updated"
              value={updatedPreset}
              onChange={(event) => setUpdatedPreset(event.target.value as InboxUpdatedPreset)}
              className="h-full border-0 bg-transparent text-[12.5px] font-medium text-npc-lulu-t1 outline-none"
            >
              <option value="any">{pj('pjshell.task.filter.updated.any')}</option>
              <option value="hour">{pj('pjshell.task.filter.updated.hour')}</option>
              <option value="today">{pj('pjshell.task.filter.updated.today')}</option>
              <option value="week">{pj('pjshell.task.filter.updated.week')}</option>
            </select>
          </label>
        </div>

        {items.length === 0 ? (
          <EmptyState
            title={<PJ id="pjshell.task.empty" />}
            body={<T id="development_task.create.subtitle" />}
          />
        ) : (
          <DataTable
            testid="project-development-tasks"
            grid={TASK_GRID}
            hint={<PJ id="pjshell.task.sort.hint" />}
            columns={[
              <PJ key="name" id="pjshell.task.col.name" />,
              <PJ key="source" id="pjshell.task.col.source" />,
              <PJ key="who" id="pjshell.task.col.who" />,
              <span key="next">{t('development_task.section.status')}</span>,
              <PJ key="elapsed" id="pjshell.task.col.elapsed" />,
              <PJ key="files" id="pjshell.task.col.files" />,
            ]}
          >
            {visible.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                message={latestByTask.get(task.id)?.message}
              />
            ))}
          </DataTable>
        )}
      </div>
    </ModulePage>
  )
}

function filterLabel(key: InboxFilter) {
  if (key === 'all') return <PJ id="pjshell.task.filter.all" />
  return <T id={userStateCopy(key)} />
}

function nextTone(state: TaskUserState): 'ok' | 'need' | 'neutral' | 'muted' {
  // Soft Structuralism: green only 已上线; coral soft 需处理; grey 进行中/待领取; muted 已取消.
  if (state === 'needs_action') return 'need'
  if (state === 'live') return 'ok'
  if (state === 'cancelled') return 'muted'
  return 'neutral'
}

function sourceCopy(source: DevelopmentTask['source']): PjshellId | null {
  if (source === 'user') return 'pjshell.task.source.user'
  if (source === 'lulu') return 'pjshell.task.source.lulu'
  return null
}

function whoDoing(task: DevelopmentTask): string | null {
  const executor = task.current_attempt?.executor_model?.trim()
  if (executor) return executor
  const model = task.developer_model?.trim()
  return model || null
}

function TaskRow({
  task,
  message,
}: {
  task: DevelopmentTask
  message?: string
}) {
  const next = taskNextActionOf(task)
  const userState = taskUserStateOf(task)
  const stale = Boolean(task.stale)
  const files = task.context_items.filter((item) => item.kind === 'code_path').map((item) => item.title)
  const warn = userState === 'needs_action'
  const source = sourceCopy(task.source)
  const who = whoDoing(task)
  const elapsedMs = visibleTaskElapsedMs({ elapsed_ms: task.elapsed_ms, stale, state: task.state })
  return (
    <Link
      to="/development-tasks/$taskId"
      params={{ taskId: task.id }}
      data-testid={`project-task-${task.id}`}
      className={`${dataRowClass(warn)} max-[390px]:grid-cols-1 max-[390px]:gap-npc-gap-xs`}
      style={{ gridTemplateColumns: TASK_GRID }}
    >
      <DataCell>
        <div className="flex min-w-0 flex-col gap-[3px]">
          <span className="text-[14px] font-semibold tracking-[-0.015em] leading-[1.4] text-npc-lulu-t1">{task.title}</span>
          {message ? <span className="text-[12.5px] font-medium leading-[1.45] text-npc-lulu-t3">{message}</span> : null}
        </div>
      </DataCell>
      <DataCell>
        <span className="max-[390px]:hidden">{source ? <PJ id={source} /> : <Unverified />}</span>
      </DataCell>
      <DataCell>
        <span className="max-[390px]:hidden">{who ?? <Unverified />}</span>
      </DataCell>
      <DataCell>
        <StatusPill tone={nextTone(next)}>
          <span data-testid={`project-task-next-${task.id}`}>
            <T id={userStateCopy(next)} />
          </span>
        </StatusPill>
      </DataCell>
      <DataCell>
        <span className="max-[390px]:hidden">
          {stale ? (
            <PJ id="pjshell.task.stale" />
          ) : elapsedMs != null ? (
            <PJ id="pjshell.task.elapsed" vars={{ elapsed: duration(Math.max(0, Math.floor(elapsedMs / 1000))) }} />
          ) : (
            <Unverified />
          )}
        </span>
      </DataCell>
      <DataCell>
        <span className="max-[390px]:hidden">{files.length > 0 ? files.join(' · ') : <Unverified />}</span>
      </DataCell>
    </Link>
  )
}
